class MainCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.abortController = null;
    this.infiniteScrollObserver = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handlePopState = this.handlePopState.bind(this);

    this.addEventListener("click", this.handleClick);
    this.addEventListener("change", this.handleChange);
    this.addEventListener("keydown", this.handleKeyDown);
    this.addEventListener("submit", this.handleSubmit);
    document.addEventListener("click", this.handleDocumentClick);
    window.addEventListener("popstate", this.handlePopState);

    this.initInfiniteScroll();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("change", this.handleChange);
    this.removeEventListener("keydown", this.handleKeyDown);
    this.removeEventListener("submit", this.handleSubmit);
    document.removeEventListener("click", this.handleDocumentClick);
    window.removeEventListener("popstate", this.handlePopState);
    this.disconnectInfiniteScroll();
    this.abortController?.abort();
  }

  handleClick(event) {
    const sortToggle = event.target.closest("[data-sort-toggle]");

    if (sortToggle && this.contains(sortToggle)) {
      event.preventDefault();
      this.toggleSortDropdown(sortToggle);
      return;
    }

    const sortOption = event.target.closest("[data-sort-option]");

    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      this.selectSortOption(sortOption);
      return;
    }

    if (!event.target.closest("[data-sort-custom]")) {
      this.closeSortDropdowns();
    }

    const filterLink = event.target.closest("[data-filter-link]");

    if (filterLink && this.contains(filterLink)) {
      event.preventDefault();
      const url = new URL(filterLink.href);

      url.searchParams.delete("page");
      this.renderPage(url.toString(), { updateHistory: true });
      return;
    }

    const paginationLink = event.target.closest(".main-collection__pagination a");

    if (paginationLink && this.contains(paginationLink)) {
      event.preventDefault();
      const append = paginationLink.closest(".next") !== null;

      this.renderPage(paginationLink.href, { append, updateHistory: !append });
    }
  }

  handleChange(event) {
    if (!event.target.matches("[data-sort-select]")) {
      return;
    }

    this.updateSortCustom(event.target.form, event.target.value);
    this.renderPage(this.getSortUrl(event.target), { updateHistory: true });
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) {
      return;
    }

    this.closeSortDropdowns();
  }

  handleKeyDown(event) {
    if (event.key !== "Escape" || !event.target.closest("[data-sort-custom]")) {
      return;
    }

    const sortCustom = event.target.closest("[data-sort-custom]");
    const sortToggle = sortCustom?.querySelector("[data-sort-toggle]");

    this.closeSortDropdowns();
    sortToggle?.focus();
  }

  handleSubmit(event) {
    if (!event.target.matches("[data-sort-form]")) {
      return;
    }

    event.preventDefault();

    const sortSelect = event.target.querySelector("[data-sort-select]");

    if (sortSelect) {
      this.renderPage(this.getSortUrl(sortSelect), { updateHistory: true });
    }
  }

  handlePopState() {
    this.renderPage(window.location.href);
  }

  getSortUrl(sortSelect) {
    const url = new URL(window.location.href);

    url.searchParams.set(sortSelect.name, sortSelect.value);
    url.searchParams.delete("page");

    return url.toString();
  }

  toggleSortDropdown(sortToggle) {
    const sortCustom = sortToggle.closest("[data-sort-custom]");
    const sortList = sortCustom?.querySelector("[data-sort-list]");
    const shouldOpen = sortToggle.getAttribute("aria-expanded") !== "true";

    this.closeSortDropdowns(sortCustom);

    if (!sortList) {
      return;
    }

    sortToggle.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
    sortList.hidden = !shouldOpen;
  }

  closeSortDropdowns(exceptSortCustom) {
    this.querySelectorAll("[data-sort-custom]").forEach((sortCustom) => {
      if (exceptSortCustom && sortCustom === exceptSortCustom) {
        return;
      }

      const sortToggle = sortCustom.querySelector("[data-sort-toggle]");
      const sortList = sortCustom.querySelector("[data-sort-list]");

      sortToggle?.setAttribute("aria-expanded", "false");

      if (sortList) {
        sortList.hidden = true;
      }
    });
  }

  selectSortOption(sortOption) {
    const sortForm = sortOption.closest("[data-sort-form]");
    const sortSelect = sortForm?.querySelector("[data-sort-select]");

    if (!sortSelect) {
      return;
    }

    sortSelect.value = sortOption.dataset.sortValue;
    this.updateSortCustom(sortForm, sortSelect.value);
    this.closeSortDropdowns();
    sortSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  updateSortCustom(sortForm, selectedValue) {
    const sortToggleText = sortForm?.querySelector("[data-sort-toggle-text]");

    sortForm?.querySelectorAll("[data-sort-option]").forEach((sortOption) => {
      const isSelected = sortOption.dataset.sortValue === selectedValue;

      sortOption.classList.toggle("is-active", isSelected);
      sortOption.setAttribute("aria-selected", isSelected ? "true" : "false");

      if (isSelected && sortToggleText) {
        sortToggleText.textContent = sortOption.textContent.trim();
      }
    });
  }

  async renderPage(url, options = {}) {
    const { append = false, updateHistory = false } = options;

    if (!this.sectionId || this.classList.contains("is-loading-more")) {
      return;
    }

    if (!append) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.classList.add("is-loading");
      this.setAttribute("aria-busy", "true");
    } else {
      this.classList.add("is-loading-more");
    }

    try {
      const freshSection = await this.fetchSection(
        url,
        append ? undefined : this.abortController.signal
      );

      if (!freshSection) {
        window.location.href = url;
        return;
      }

      if (append) {
        this.appendProducts(freshSection);
      } else {
        this.replaceContent(freshSection);
      }

      if (updateHistory) {
        window.history.pushState({}, "", this.cleanUrl(url));
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Collection update failed:", error);
        window.location.href = url;
      }
    } finally {
      this.classList.remove("is-loading", "is-loading-more");
      this.removeAttribute("aria-busy");
      this.initInfiniteScroll();
    }
  }

  async fetchSection(url, signal) {
    const requestUrl = new URL(url, window.location.origin);

    requestUrl.searchParams.set("section_id", this.sectionId);

    const response = await fetch(requestUrl.toString(), {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    const text = await response.text();
    const documentFragment = new DOMParser().parseFromString(text, "text/html");

    return documentFragment.querySelector("main-collection");
  }

  replaceContent(freshSection) {
    this.innerHTML = freshSection.innerHTML;
  }

  appendProducts(freshSection) {
    const currentGrid = this.querySelector("[data-product-grid]");
    const freshGrid = freshSection.querySelector("[data-product-grid]");
    const currentGridWrap = this.querySelector(".main-collection__grid-wrap");
    const freshGridWrap = freshSection.querySelector(".main-collection__grid-wrap");
    const currentPagination = this.querySelector("[data-pagination]");
    const freshPagination = freshSection.querySelector("[data-pagination]");

    if (!currentGrid || !freshGrid) {
      if (freshGridWrap) {
        currentGridWrap?.replaceWith(freshGridWrap);
      }

      return;
    }

    Array.from(freshGrid.children).forEach((item) => {
      currentGrid.appendChild(item);
    });

    if (freshPagination) {
      currentPagination?.replaceWith(freshPagination);
    } else {
      currentPagination?.remove();
    }
  }

  initInfiniteScroll() {
    this.disconnectInfiniteScroll();
    this.classList.remove("is-infinite-ready");

    const pagination = this.querySelector("[data-pagination]");
    const nextUrl = pagination?.dataset.nextUrl;

    if (!pagination || !nextUrl || !("IntersectionObserver" in window)) {
      return;
    }

    this.classList.add("is-infinite-ready");

    this.infiniteScrollObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.disconnectInfiniteScroll();
        this.renderPage(nextUrl, { append: true });
      },
      { rootMargin: "600px 0px" }
    );

    this.infiniteScrollObserver.observe(pagination);
  }

  disconnectInfiniteScroll() {
    this.infiniteScrollObserver?.disconnect();
    this.infiniteScrollObserver = null;
  }

  cleanUrl(url) {
    const cleanUrl = new URL(url, window.location.origin);

    cleanUrl.searchParams.delete("section_id");

    return `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`;
  }
}

if (!customElements.get("main-collection")) {
  customElements.define("main-collection", MainCollection);
}
